import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router';
import { useUser } from '../contexts/UserContext';
import { legalPageUrl, isLegalPath } from '../legal/legalLinks';
import LoadingState from '../lsc/components/LoadingState';

interface TermsAcceptanceGateProps {
  children: React.ReactNode;
}

export function TermsAcceptanceScreen() {
  const { user, acceptTerms } = useUser();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const termsUrl = legalPageUrl('terms');
  const privacyUrl = legalPageUrl('privacy');

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!checked || submitting) {
        return;
      }
      setSubmitting(true);
      setError(null);
      try {
        await acceptTerms();
      } catch (submitError) {
        console.error('Terms acceptance failed:', submitError);
        setError('Could not save your acceptance. Please try again.');
        setSubmitting(false);
      }
    },
    [acceptTerms, checked, submitting]
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white"
      data-testid="terms-acceptance-screen"
    >
      <div className="max-w-lg w-full card p-8">
        <h1 className="text-2xl font-bold mb-3">Welcome to Scoutlytics</h1>
        <p className="text-secondary-600 dark:text-secondary-300 mb-6">
          {user?.display_name
            ? `Hi ${user.display_name}, before you continue`
            : 'Before you continue'}
          , please confirm that you have read and agree to our Terms of Service and
          Privacy Policy.
        </p>
        <form onSubmit={handleSubmit} className="space-y-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="terms-acceptance-checkbox"
              checked={checked}
              onChange={(event) => setChecked(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-secondary-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-secondary-700 dark:text-secondary-200 leading-relaxed">
              I have read and agree to the{' '}
              <a
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 dark:text-purple-400 underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 dark:text-purple-400 underline"
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="terms-acceptance-continue"
            disabled={!checked || submitting}
            className="w-full inline-flex items-center justify-center bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

const TermsAcceptanceGate: React.FC<TermsAcceptanceGateProps> = ({ children }) => {
  const { user, loading } = useUser();
  const location = useLocation();

  if (loading) {
    return <LoadingState message="Checking sign-in status..." />;
  }

  if (!user || user.accepted_terms_at || isLegalPath(location.pathname)) {
    return <>{children}</>;
  }

  return <TermsAcceptanceScreen />;
};

export default TermsAcceptanceGate;
